/**
 * The values every page was re-declaring.
 *
 * There is no colour, spacing or radius scale in `theme/index.js`, so each screen invented its own:
 * the mapping page keeps a local `C` map, the project list hardcodes `#495057` / `#ADB5BD` / `#CED4DA`
 * inline, the jurisdictions table carries about twenty raw hexes across two files. Same greys, written
 * five ways, drifting apart one edit at a time.
 *
 * These are the Mantine ramp values already in use — nothing new is introduced here, the existing
 * literals are just given one home. Import the token instead of typing the hex.
 */

export const C = {
  blue7: '#1971C2',
  blue6: '#228BE6',
  blue5: '#228BE6',
  blue4: '#4DABF7',
  blue0: '#E7F5FF',
  teal5: '#20C997',
  green7: '#2F9E44',
  green2: '#B2F2BB',
  green0: '#EBFBEE',
  red6: '#FA5252',
  gray0: '#F8F9FA',
  gray1: '#F1F3F5',
  gray2: '#E9ECEF',
  gray3: '#DEE2E6',
  gray4: '#CED4DA',
  gray5: '#ADB5BD',
  gray6: '#868E96',
  gray7: '#495057',
  dark7: '#141517',
  white: '#FFFFFF'
};

export const FONT_FAMILY = 'Roboto, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif';

/**
 * Row rhythm. A list row is a full-width band with one padding and one radius, so hover and selection
 * cover the whole row instead of a strip inside it.
 */
export const ROW = {
  paddingY: 7,
  paddingX: 8,
  radius: 6,
  gap: 8
};

/** Header strips (page headers, table toolbars) — one box so adjacent ones line up across a split. */
export const HEADER = {
  padding: '6px 12px',
  minHeight: 39
};

/**
 * Hover is not selection. They were painted the same blue on the mapping list, so pointing at a row
 * looked like having chosen it; on the project list the hover rule is commented out entirely and
 * hovered, selected and idle rows are pixel-identical.
 */
export const ROW_STATE = {
  hover: C.gray0,
  selected: C.blue0,
  transition: 'background 150ms ease'
};

/** One icon size for a row-level control. `size` and `fontSize` were mixed for the same library. */
export const ICON = {
  sm: 16,
  xs: 14
};

export const RADIUS = {
  card: 8,
  control: 4
};

/**
 * Height of the app chrome above a full-page surface. Three different numbers were subtracted from
 * 100vh for the same bars (68 / 115 / 130), all written at different times.
 */
export const CHROME_OFFSET = 68;
